import React from "react";
import "./AddOnsSection.css";
import { toast } from "react-toastify";

const BASE_PRICES = {
  monthly: { starter: 14, pro: 37, advanced: 224 },
  annual: { starter: 11, pro: 30, advanced: 179 }, // effective per-month
};

const pricePerMonth = (monthly, cycle) =>
  cycle === "annual" ? (monthly * 10) / 12 : monthly; // 2 months free on annual

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function AddOnsSection({
  activePlan = "pro",
  onChangePlan,             // 'starter' | 'pro' | 'advanced'
  currentPlan,                      // optional: { planId, status }
  onContinue,                       // (payload) => void
}) {

  const [cycle, setCycle] = React.useState("monthly"); // 'monthly' | 'annual'

  // quantities
  const [uploadPacks, setUploadPacks] = React.useState(0); // $10 / 20M chars
  const [teamAdmins, setTeamAdmins] = React.useState(0);    // $25 / admin

  // toggles
  const [hideBranding, setHideBranding] = React.useState(false);   // $49
  const [apiAccess, setApiAccess] = React.useState(false);         // $49
  const [extraBots, setExtraBots] = React.useState(false);         // $99 (3 bots)
  const [supportHandoff, setSupportHandoff] = React.useState(false); // $199
  const MESSAGE_STEPS = [0, 2000, 4000, 8000, 16000];
  const [messageIndex, setMessageIndex] = React.useState(0);
  const messageAddonMonthly = (messageIndex / 10) * 10;
  const messages = MESSAGE_STEPS[messageIndex];

  const addonMonthlyRaw =
    messageAddonMonthly * 10 +
    uploadPacks * 8 +
    teamAdmins * 20 +
    (hideBranding ? 39 : 0) +
    (apiAccess ? 39 : 0) +
    (extraBots ? 75 : 0) +
    (supportHandoff ? 160 : 0);

  const addonPerMonth = Number(pricePerMonth(addonMonthlyRaw, cycle).toFixed(2));
  const basePerMonth = BASE_PRICES[cycle][activePlan];
  const totalPerMonth = Number((basePerMonth + addonPerMonth).toFixed(2));
  const totalPerYear = Number((totalPerMonth * 12).toFixed(2));

  const isCurrent =
    currentPlan?.status === "active" && currentPlan?.planId === activePlan;

  const handleContinue = async () => {
    try {
      const payload = {
        email: currentPlan?.email,
        addOns: {
          planId: activePlan,
          chatbots: extraBots ? 3 : 0,
          messagesPerMonth: messages,
          uploadChars: uploadPacks * 20000000,
          teamAdmins: teamAdmins,
          isHideBranding: hideBranding,
          allowAPIAccess: apiAccess,
          supportHandOff: supportHandoff,
          addOnsPrice: totalPerMonth,
        }
      };
      console.log("Payload for add-ons update:", payload);
      const res = await fetch(`${API_BASE_URL}/api/auth/manage-plan/add-ons`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      toast.success("Add-ons updated successfully!");
      
    } catch (error) {
      toast.error("Failed to update add-ons: " + error.message);
      return;
    }
    }

  return (
    <div className="addons-wrap">
      {/* Left: tabs + controls */}
      <div className="addons-left">
        <div className="plan-tabs" role="tablist">
          {["starter", "pro", "advanced"].map((p) => (
            <button
              key={p}
              role="tab"
              className={`tab-btn ${activePlan === p ? "active" : ""}`}
              onClick={() => onChangePlan?.(p)}
            >
              {p.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="panel">
          <h3 className="section-title">Paid add-ons</h3>

          {/* Messages slider (visual) */}
          <div className="slider-row">
            <div className="slider-top">
              <span>{messages.toLocaleString()} messages / month</span>
            </div>
            <input
              type="range"
              min={0}
              max={MESSAGE_STEPS.length - 1}
              step={1}
              value={messageIndex}
              onChange={(e) => setMessageIndex(Number(e.target.value))}
              className="range"
            />
            <div className="ticks">
              {MESSAGE_STEPS.map((t) => (
                <span key={t}>{t === 0 ? "0k" : t / 1000 + "k"}</span>
              ))}
            </div>
          </div>

          {/* Quantities */}
          <AddonQty
            label="Number of uploaded characters"
            note="$8 / 20M characters / month"
            value={uploadPacks}
            onDec={() => setUploadPacks(Math.max(0, uploadPacks - 1))}
            onInc={() => setUploadPacks(uploadPacks + 1)}
          />

          <AddonQty
            label="Number of team admin(s)"
            note="$20 / team admin / month"
            value={teamAdmins}
            onDec={() => setTeamAdmins(Math.max(0, teamAdmins - 1))}
            onInc={() => setTeamAdmins(teamAdmins + 1)}
          />

          {/* Toggles */}
          <AddonToggle
            label="Hide Branding"
            note="$39 / month"
            checked={hideBranding}
            onChange={() => setHideBranding((v) => !v)}
          />
          <AddonToggle
            label="Allow API Access"
            note="$39 / month"
            checked={apiAccess}
            onChange={() => setApiAccess((v) => !v)}
          />
          <AddonToggle
            label="3 Additional bots"
            note="$75 / month"
            checked={extraBots}
            onChange={() => setExtraBots((v) => !v)}
          />
          <AddonToggle
            label="Enable Support Handoff Integration(s)"
            note="$160 / month"
            checked={supportHandoff}
            onChange={() => setSupportHandoff((v) => !v)}
          />
        </div>
      </div>

      {/* Right: summary card */}
      <aside className="addons-right">
        <div className="billing-toggle">
          <button
            className={`bill-btn ${cycle === "monthly" ? "active" : ""}`}
            onClick={() => setCycle("monthly")}
          >
            Monthly
          </button>
          <button
            className={`bill-btn ${cycle === "annual" ? "active" : ""}`}
            onClick={() => setCycle("annual")}
          >
            Annual
          </button>
          <span className="badge-free">Get 2 months free</span>
        </div>

        <div className="summary">
          <h4 className="summary-title">Plan details</h4>
          <div className="price-line">
            <span className="plan-name">{activePlan.toUpperCase()} plan</span>
            <span className="plan-price">${basePerMonth} / month</span>
          </div>

          <ul className="plan-points">
            {activePlan === "starter" && (
              <>
                <li>1000 messages</li>
                <li>10M characters</li>
                <li>1 bot</li>
                <li>1 seat</li>
              </>
            )}
            {activePlan === "pro" && (
              <>
                <li>3000 messages</li>
                <li>50M characters</li>
                <li>2 bots</li>
                <li>2 seats</li>
              </>
            )}
            {activePlan === "advanced" && (
              <>
                <li>12000 messages</li>
                <li>100M characters</li>
                <li>2 bots</li>
                <li>5 seats</li>
              </>
            )}
          </ul>

          <div className="splitter" />

          <div className="price-line">
            <span>Paid add-ons</span>
            <span>${addonPerMonth} / month</span>
          </div>

          <div className="grand">
            <span>Total</span>
            <div className="grand-amt">
              {cycle === "monthly" ? (
                <>
                  <div>${totalPerMonth} / month</div>
                  <div className="muted">${totalPerYear} billed yearly</div>
                </>
              ) : (
                <>
                  <div>${totalPerMonth} / month</div>
                  <div className="muted">
                    ${Number((totalPerMonth * 12).toFixed(2))} billed annually
                  </div>
                </>
              )}
            </div>
          </div>

          <button
            disabled={isCurrent}
            className={`continue-btn ${isCurrent ? "disabled" : ""}`}
            onClick={handleContinue}
          >
            {isCurrent ? "Current Plan" : "Continue"}
          </button>
        </div>
      </aside>
    </div>
  );
}

function AddonQty({ label, note, value, onDec, onInc }) {
  return (
    <div className="qty-row">
      <div className="qty-info">
        <div className="qty-label">{label}</div>
        <div className="qty-note">{note}</div>
      </div>
      <div className="qty-ctrl">
        <button onClick={onDec} className="qty-btn">−</button>
        <span className="qty-value">{value}</span>
        <button onClick={onInc} className="qty-btn">+</button>
      </div>
    </div>
  );
}

function AddonToggle({ label, note, checked, onChange }) {
  return (
    <div className="toggle-row">
      <div className="toggle-info">
        <div className="toggle-label">{label}</div>
        <div className="toggle-note">{note}</div>
      </div>
      <label className="switch">
        <input type="checkbox" checked={checked} onChange={onChange} />
        <span className="slider" />
      </label>
    </div>
  );
}
